/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, ReactNode, useState, useEffect, useMemo } from "react"
import { SortableContainer, SortableContainerProps } from "react-sortable-hoc"
import { FixedSizeList as List, ListChildComponentProps } from "react-window"
import AutoSizer from "react-virtualized-auto-sizer"
import arrayMove from "array-move"

import { sortDiffList } from "utils/misc"

import { SortableItemRow } from "./SortableItemRow"

const defaultRowHeight = 64

interface RowData {
  items: any[]
  useDragHandle?: boolean
  renderRowContent: (item: any, index: number) => ReactNode
  onRowClick?: (event: React.MouseEvent<HTMLElement>, record: any) => void
  isItemSelected?: (item: any) => boolean
  draggable: boolean
  renderLoadMore?: () => ReactNode
}

type RowProps = Omit<ListChildComponentProps, "data"> & { data: RowData }

const Row: FC<RowProps> = ({ data, index, style }) => {
  const {
    items,
    useDragHandle,
    renderRowContent,
    onRowClick,
    isItemSelected,
    draggable,
    renderLoadMore,
  } = data

  const isLoadMoreRow = renderLoadMore && index === items.length

  if (isLoadMoreRow) {
    return <div style={style}>{renderLoadMore()}</div>
  }

  return (
    <SortableItemRow
      useDragHandle={useDragHandle}
      style={style}
      index={index}
      item={items[index]}
      renderRowContent={item => renderRowContent(item, index)}
      onClick={onRowClick}
      isItemSelected={isItemSelected}
      draggable={draggable}
    />
  )
}

export interface ItemListProps extends SortableContainerProps {
  items: any[]
  itemSize?: number
  onSort?({
    oldIndex,
    newIndex,
    oldList,
    newList,
  }: {
    oldIndex: number
    newIndex: number
    oldList: any[]
    newList: any[]
  }): void
  onClear?(): void
  onClose?(): void
  onSave?(sortedItems: any[]): void
  onRowClick?(event: React.MouseEvent<HTMLElement>, record: any): void
  renderRowContent(item: any, index: number): ReactNode
  renderFooter?({
    oldList,
    newList,
  }: {
    oldList: any[]
    newList: any[]
  }): ReactNode
  renderLoadMore?: () => ReactNode
  backgroundColor?: string
  isItemSelected?(item: any): boolean
  draggable?: boolean
}

const SortableList = SortableContainer(
  ({ children }: { children: ReactNode }) => {
    return <div>{children}</div>
  },
)

export const SortableItemList: FC<ItemListProps> = ({
  items,
  itemSize = defaultRowHeight,
  renderRowContent,
  onSort,
  onClear,
  onRowClick,
  useDragHandle,
  renderFooter,
  renderLoadMore,
  transitionDuration = 300,
  backgroundColor,
  isItemSelected,
  draggable = false,
}: ItemListProps) => {
  const [initialItems, setInitialItems]: [any[], Function] = useState([])
  const [sortedItems, setSortedItems]: [any[], Function] = useState([])

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => {
    const newList = arrayMove(sortedItems, oldIndex, newIndex)

    if (
      sortDiffList({
        oldList: initialItems,
        newList,
      }).length
    ) {
      onSort?.({
        oldIndex,
        newIndex,
        oldList: initialItems,
        newList,
      })
    } else {
      onClear?.()
    }
    return setSortedItems(newList)
  }

  useEffect(() => {
    setInitialItems(items)
    setSortedItems(items)
  }, [items])

  const itemData = useMemo<RowData>(
    () => ({
      items: sortedItems,
      useDragHandle,
      renderRowContent,
      onRowClick,
      isItemSelected,
      draggable,
      renderLoadMore,
    }),
    [
      sortedItems,
      useDragHandle,
      renderRowContent,
      onRowClick,
      isItemSelected,
      draggable,
      renderLoadMore,
    ],
  )

  return (
    <div
      css={css`
        height: 100%;
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          background-color: ${backgroundColor};
          flex: 1 0 auto;
        `}
      >
        <AutoSizer data-testid="auto-sizer">
          {({ height, width }: any) => (
            <SortableList
              onSortEnd={onSortEnd}
              lockAxis="y"
              useDragHandle={useDragHandle}
              helperClass="sortable-list"
              transitionDuration={transitionDuration}
            >
              <List
                height={height}
                width={width}
                itemCount={sortedItems.length + (renderLoadMore ? 1 : 0)}
                itemSize={itemSize}
                itemData={itemData}
              >
                {Row}
              </List>
            </SortableList>
          )}
        </AutoSizer>
      </div>
      {renderFooter?.({
        oldList: initialItems,
        newList: sortedItems,
      })}
    </div>
  )
}
